// this file contains all regex patterns and pattern-building functions used in tokenization and text processing

// ToDo: should some digits be considered as graphemes?
//  0>o, 3>e, 5>s or z etc?
export const validGraphemes: string = 'abcdefghijklmnopqrstuvwxyz';
export const INVALID_GRAPHEME_REGEX: RegExp = new RegExp(`[^${validGraphemes}]`, 'g');

const LOWER = 'a-z';
const UPPER = 'A-Z';
const DIGITS = '0-9';
const EXTENDED_LATIN = 'À-ÖØ-öø-ɏ'; // precomposed accented Latin chars (é, à, ü, ñ, ç …) — skips × (U+00D7) and ÷ (U+00F7)

const COMBINING_DIACRITICS = `\u0300-\u036f`; // unicode range for combining accentts/diacritical marks

const APOSTROPHES = "'’‘‛ʼʹ´`"; // all apostrophe variants
const HYPHENS = `\\-‐-‒–—`; // ASCII + Unicode dashes

export const ALPHA = `${LOWER}${UPPER}${EXTENDED_LATIN}`;
// const LOWERNUMERIC = `${LOWER}${DIGITS}`;
const UPPERNUMERIC = `${UPPER}${DIGITS}`;
const ALPHANUMERIC = `${ALPHA}${DIGITS}`;

const VALID_TOKEN_CHAR = `${ALPHANUMERIC}${APOSTROPHES}${HYPHENS}`;

const QUOTES = `"'“”‘’«»‹›\``; // straight, curly, angled, backtick

export const CLAUSE_DELIMITERS = `,;:`;
export const SENTENCE_TERMINATORS = `.?!…‽؟۔！？`;
export const TRAILING_PUNCTUATION = `${CLAUSE_DELIMITERS}${SENTENCE_TERMINATORS}`;

const SOCIAL_PREFIX = '@#+';
const CURRENCY_PREFIX = '$€£';

// export the global (/g) as functions to ensure each import uses a distinct RegExp instance
export const createValidTokenCharRe = () => new RegExp(`[${VALID_TOKEN_CHAR}]+`, 'g');
export const createInvalidTokenCharRe = () => new RegExp(`[^${VALID_TOKEN_CHAR}]+`, 'g');
export const createCombiningDiacriticsRe = () => new RegExp(`[${COMBINING_DIACRITICS}]`, 'g');
export const createHyphenRe = () => new RegExp(`[${HYPHENS}]`, 'g');
export const createQuotesRe = () => new RegExp(`[${QUOTES}]`, 'g');
export const createApostropheRe = () => new RegExp(`[${APOSTROPHES}]`, 'g');
// Matches apostrophes that should be removed (quotes, isolated marks, etc.)
export const createFindApostropheQuotesRe = () =>
  new RegExp(
    // opening quote before a word
    `(?<=\\s)[${APOSTROPHES}](?=[${ALPHANUMERIC}])` +
      // or closing quote after a word
      `|(?<=[${ALPHANUMERIC}])[${APOSTROPHES}](?=\\s|$)` +
      // or apostrophe not between alphanumerics
      `|(?<![${ALPHANUMERIC}])[${APOSTROPHES}](?![${ALPHANUMERIC}])`,
    'g',
  );

/**
 * Matches contiguous runs of non-whitespace characters.
 * Used to split text on whitespace boundaries during initial tokenization.
 */
export const createSplitOnWhitespaceRe = () => /(\S+)/g;

// --- abbreviation patterns ---
//const SINGLE_ABBREVIATION_PATTERN = `\\b[${ALPHANUMERIC}]{1,3}\\.`; // e.g. Dr., Mr., Ms.
const SINGLE_ABBREVIATION_PATTERN = `\\b[${UPPERNUMERIC}][${ALPHANUMERIC}]{0,2}\\.`; // e.g. Dr., Mr., Ms. - requires upper/numeric first char - avoids matching sentence ending in '.. now.' for example
const MULTI_ABBREVIATION_PATTERN = `\\b[${ALPHANUMERIC}]+\\.(?:[${ALPHANUMERIC}]+\\.)+`; // e.g. U.S.A., I.e., i.e., etc.

// individual regexes
export const SINGLE_ABBREVIATION_RE = new RegExp(SINGLE_ABBREVIATION_PATTERN);
export const MULTI_ABBREVIATION_RE = new RegExp(MULTI_ABBREVIATION_PATTERN);

// compound regex that matches either
export const ABBREVIATION_RE = new RegExp(`(?:${SINGLE_ABBREVIATION_PATTERN}|${MULTI_ABBREVIATION_PATTERN})`);

export const createAbbreviationRe = () =>
  new RegExp(
    `(?:${SINGLE_ABBREVIATION_PATTERN}|${MULTI_ABBREVIATION_PATTERN})`,
    'gu', // global + unicode
  );

// --- special tokenn-like pattern helpers ---
const SOCIALS_RE = new RegExp(`^[${SOCIAL_PREFIX}][\\w-]+$`); // @user or #topic
const URL_RE = /\bhttps?:\/\/[^\s]+/i;
const EMAIL_RE = /\b[\w._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/;
const IPV4_RE = /\b\d{1,3}(?:\.\d{1,3}){3}\b/;
const IPV6_RE = /\b[0-9A-Fa-f]{1,4}(?::[0-9A-Fa-f]{0,4}){2,}\b/;
const IP_LITERAL_RE = /\b(?:IP)[vV]?(?:4|6)?\b/;
const PHONE_RE = /\+?\d[\d\-() ]{5,}\d\b/;
const CURRENCY_RE = new RegExp(`[${CURRENCY_PREFIX}]\\d+(?:\\.\\d+)?`);
const DEGREE_RE = /\b\d+(?:\.\d+)?°\b/;

const ELLIPSIS_RE = /\.{2,}|…+/; // matches consecutive elipsis: … .. ... .... etc.

// Matches short uppercase tokens (1–2 chars) representing units, symbols,
// or directional markers (e.g. S, E, N, W, V, R, L, etc.).
// Excludes "A" and "I" (valid English words).
// Allows an optional trailing punctuation mark drawn from TRAILING_PUNCTUATION.
export const UNIT_OR_SYMBOL_RE = new RegExp(`\\b[B-HJ-Z]{1,2}[${TRAILING_PUNCTUATION}]?\\b`);

// --- consolidated list ---
export const SPECIAL_PATTERNS = [
  URL_RE,
  EMAIL_RE,
  SOCIALS_RE,
  IP_LITERAL_RE,
  IPV4_RE,
  IPV6_RE,
  PHONE_RE,
  CURRENCY_RE,
  DEGREE_RE,
  ABBREVIATION_RE,
  ELLIPSIS_RE, // not sure this belongs here ... special patterns captures token-like patterns, but ellipsis is more of a sentence/syntax pattern
  UNIT_OR_SYMBOL_RE,
];
